import {
  CustomQueryBuilder,
  type GetPlacesCheckByPlaceIdResponse,
  useSessionToken,
} from "@birdwatch/client";
import { useMutation } from "@tanstack/react-query";

/**
 * Whether a place already has an active membership — the second half of the duplicate
 * check, run on the `placeId` that `validate-address` resolved.
 *
 * Modelled as a mutation because it fires from a submit, not from render: the id is only
 * known once the address has been checked. The generated `useGetPlacesCheckByPlaceId` is a
 * query builder with no imperative entry point, and calling it here would be a hook inside
 * a callback — so the builder is constructed directly, the same shape `usePresaleCoupon`
 * uses for the same problem. Uncached by design: a duplicate check must read the answer as
 * of this submit.
 */
export const usePlaceCheck = () => {
  const getToken = useSessionToken();

  return useMutation({
    mutationFn: async (placeId: string) => {
      const builder = new CustomQueryBuilder<GetPlacesCheckByPlaceIdResponse>(
        "places",
        "/:placeId/check",
        { placeId },
      );
      const token = await getToken();
      const result = await builder.fetch(token);

      return { isActive: result.data?.isActive ?? false };
    },
    mutationKey: ["place-check"],
  });
};
